/* eslint-disable no-console -- TODO @Evgeny remove logs */

import { useAppDispatch, useAppSelector } from 'hooks';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  selectConversationId,
  selectFeatureFlags,
  selectForethoughtLiveChatWidgetComponent,
} from 'reducers/workflows';

import { selectIsCurrentTabPrimary } from 'persistence/persistenceBetweenTabs';

import { selectWidgetInitMethod } from 'reducers/config';
import { useLiveChatFailure } from './useLiveChatFailure';
import * as ConversationEntryUtil from 'components/salesforce-messaging-one-chat/conversationEntryUtil';
import {
  CLIENT_CONSTANTS,
  CONVERSATION_CONSTANTS,
} from 'components/salesforce-messaging-one-chat/constants';
import { prechatUtil } from 'components/salesforce-messaging-one-chat/prechatUtil';
import {
  clearInMemoryData,
  clearWebStorage,
  getConversationId,
  getJwt,
  setDeploymentConfiguration,
  setJwt,
  setLastEventId,
  storeConversationId,
} from 'components/salesforce-messaging-one-chat/dataProvider';
import {
  createConversation,
  getAuthenticatedAccessToken,
  getContinuityJwt,
  getConversationRoutingStatus,
  getUnauthenticatedAccessToken,
  listConversationEntries,
  listConversations,
  sendMessageReadReceipt,
  sendTextMessage,
} from 'components/salesforce-messaging-one-chat/messagingService';
import {
  closeEventSource,
  eventSource,
  subscribeToEventSource,
} from 'components/salesforce-messaging-one-chat/eventSourceService';
import { util } from 'components/salesforce-messaging-one-chat/common';
import CountdownTimer from 'components/salesforce-messaging-one-chat/countdownTimer';
import {
  onAgentConnected,
  setLiveChatStatus,
  setDeploymentConfiguration as setDeploymentConfigurationAction,
} from 'reducers/live-chat';
import {
  addLiveChatEventMessage,
  addLiveChatWidgetComponents,
  concludeLiveChat,
  handleAgentTyping,
  updateForethoughtLiveChatHelpdeskConversationId,
} from 'actions/live-chat';
import type {
  ButtonComponent,
  FormFields,
  FormStepComponent,
  SalesforceMessagingLiveChatIntegrationConfig,
  UserMessageComponent,
} from 'api/types';
import type {
  ParsedDeliveryAcknowledgementEntryPayload,
  ParsedReadAcknowledgementEntryPayload,
  ParsedRoutingEntryPayload,
  SalesforceFormMessageComponent,
  Section,
  ServerSentEvent,
  TextInput,
  RoutingAttributes,
  ParsedParticipantChangedEntryPayload,
  ParsedRoutingWorkResultEntryPayload,
} from 'components/salesforce-messaging-one-chat/types';
import type { ValueOf } from 'type-fest';
import { captureMessage } from '@sentry/react';
import ApiService from 'services/ApiService';
import { sendErrorToSentry } from 'utils/sendErrorToSentry';
import { getErrorMessage } from 'utils/utils';
import { v4 as uuidv4 } from 'uuid';
import { sendTrackingEvent } from 'actions/workflows/workflows';

export const useHandleSalesforceChatInitialization = () => {
  const dispatch = useAppDispatch();
  const handleLiveChatFailure = useLiveChatFailure();
  const conversationId = useAppSelector(selectConversationId);
  const liveChatWidgetComponent = useAppSelector(
    selectForethoughtLiveChatWidgetComponent,
  );
  const isCurrentTabPrimary = useAppSelector(selectIsCurrentTabPrimary);
  const widgetInitMethod = useAppSelector(selectWidgetInitMethod);
  const featureFlags = useAppSelector(selectFeatureFlags);
  const isInitialized = useRef(false);

  // Initialize a list of conversation entries.
  const [conversationEntries, setConversationEntries] = useState<
    ReturnType<typeof ConversationEntryUtil.createConversationEntry>[]
  >([]);
  // Initialize the conversation status.
  const [conversationStatus, setConversationStatus] = useState<
    ValueOf<typeof CONVERSATION_CONSTANTS.ConversationStatus>
  >(CONVERSATION_CONSTANTS.ConversationStatus.NOT_STARTED_CONVERSATION);
  // Tracks whether Pre-Chat form should be shown.
  // eslint-disable-next-line unused-imports/no-unused-vars -- TODO @Evgeny will be needed later
  const [showPrechatForm, setShowPrechatForm] = useState(false);
  // Tracks the most recent conversation message that was failed to send.
  const [failedMessage, setFailedMessage] = useState<
    | {
        inReplyToMessageId: string;
        isNewMessagingSession: boolean;
        language: string;
        messageId: string;
        routingAttributes: RoutingAttributes;
        value: string;
      }
    | undefined
  >(undefined);
  // Initialize current participants that are actively typing (except end user).
  // Each key is the participant's `senderName` or `role` and each value is a reference to a CountdownTimer object.
  const [currentTypingParticipants] = useState<
    Record<string, { countdownTimer: CountdownTimer; role: string }>
  >({});
  // Initialize whether at least 1 participant (not including end user) is typing.
  const [isAnotherParticipantTyping, setIsAnotherParticipantTyping] =
    useState(false);
  const strictEndChatDetection = featureFlags.includes(
    'salesforce_messaging_one_chat_strict_end_chat_detection',
  );

  useEffect(() => {
    dispatch(handleAgentTyping({ isTyping: isAnotherParticipantTyping }));
  }, [dispatch, isAnotherParticipantTyping]);

  /**
   * Update conversation status state based on the event from a child component i.e. MessagingHeader.
   * Updating conversation status state re-renders the current component as well as the child components and the child components can reactively use the updated conversation status to make any changes.
   *
   * @param {string} status - e.g. CLOSED.
   */
  const updateConversationStatus = useCallback(
    (status: ValueOf<typeof CONVERSATION_CONSTANTS.ConversationStatus>) => {
      setConversationStatus(status);
    },
    [],
  );

  const endConversation = useCallback(() => {
    updateConversationStatus(
      CONVERSATION_CONSTANTS.ConversationStatus.CLOSED_CONVERSATION,
    );
    // Need preventRepeatedMessages because multiple CONVERSATION_ROUTING_WORK_RESULT can be received with different timestamps
    dispatch(
      addLiveChatEventMessage({
        message: 'Conversation ended',
        preventRepeatedMessages: true,
      }),
    );
    dispatch(concludeLiveChat());
  }, [dispatch, updateConversationStatus]);

  /**
   * Performs a cleanup in the app.
   * 1. Closes the EventSource connection.
   * 2. Clears the web storage.
   * 3. Clears the in-memory messaging data.
   * 4. Update the internal conversation status to CLOSED.
   */
  const cleanupMessagingData = useCallback(() => {
    closeEventSource()
      .then(() => console.log('Closed the Event Source (SSE).'))
      .catch((err: unknown) => {
        sendErrorToSentry({
          additionalContext: { chatId: getConversationId() },
          conversationId: conversationId,
          error: `Salesforce One Chat: Something went wrong in closing the Event Source (SSE): ${getErrorMessage(err)}`,
        });
      });

    clearWebStorage();
    clearInMemoryData();

    // Update state to conversation closed status.
    updateConversationStatus(
      CONVERSATION_CONSTANTS.ConversationStatus.CLOSED_CONVERSATION,
    );
  }, [conversationId, updateConversationStatus]);

  /**
   * Handles the errors from messaging endpoint requests.
   * If a request is failed due to an Unauthorized error (i.e. 401), peforms a cleanup and resets the app and console logs otherwise.
   */
  const handleMessagingErrors = useCallback(
    (err: unknown) => {
      if (typeof err === 'object' && err !== null) {
        if ('status' in err && err.status) {
          switch (err.status) {
            case 401:
              sendErrorToSentry({
                additionalContext: { chatId: getConversationId() },
                conversationId: conversationId,
                error: `Salesforce One Chat handoff error: Unauthenticated request: ${getErrorMessage(err)}`,
              });
              cleanupMessagingData();
              handleLiveChatFailure({
                error: `Salesforce One Chat handoff error: Unauthenticated request: ${getErrorMessage(err)}`,
                integration: 'salesforce',
              });
              break;
            case 400:
              sendErrorToSentry({
                additionalContext: { chatId: getConversationId() },
                conversationId: conversationId,
                error: `Salesforce One Chat: Invalid request parameters. Please check your data before retrying: ${getErrorMessage(err)}`,
              });
              break;
            case 404:
              sendErrorToSentry({
                additionalContext: { chatId: getConversationId() },
                conversationId: conversationId,
                error: `Salesforce One Chat: Resource not found. Please check your data before retrying: ${getErrorMessage(err)}`,
              });
              break;
            case 429:
              sendErrorToSentry({
                additionalContext: { chatId: getConversationId() },
                conversationId: conversationId,
                error: `Salesforce One Chat: Too many requests issued from the app. Try again in sometime: ${getErrorMessage(err)}`,
              });
              break;
            /**
             * HTTP error code returned by the API(s) when a message is sent and failed because no messaging session exists. This error indicates client
             * to surface the Pre-Chat form if configured to show for every new messaging session and then retry the failed message with routingAttributes.
             */
            case 417:
              if (
                prechatUtil.shouldDisplayPrechatForm() &&
                prechatUtil.shouldDisplayPrechatEveryMessagingSession()
              ) {
                console.log(
                  'Pre-Chat configured to show for every new messaging session. Continuing to display the Pre-Chat form.',
                );
                setShowPrechatForm(true);
              }
              break;
            case 500:
              sendErrorToSentry({
                additionalContext: { chatId: getConversationId() },
                conversationId: conversationId,
                error: `Salesforce One Chat: Something went wrong in the request, please try again: ${getErrorMessage(err)}`,
              });
              break;
            default:
              sendErrorToSentry({
                additionalContext: { chatId: getConversationId() },
                conversationId: conversationId,
                error: `Salesforce One Chat handoff error: Unhandled/Unknown http error: ${getErrorMessage(err)}`,
              });
              cleanupMessagingData();
              handleLiveChatFailure({
                error: `Salesforce One Chat handoff error: Unhandled/Unknown http error: ${getErrorMessage(err)}`,
                integration: 'salesforce',
              });
          }
          return;
        }
        sendErrorToSentry({
          additionalContext: { chatId: getConversationId() },
          conversationId: conversationId,
          error: `Salesforce One Chat: Something went wrong: ${getErrorMessage(err)}`,
        });
      }
      return;
    },
    [cleanupMessagingData, conversationId, handleLiveChatFailure],
  );

  /**
   * Handles creating a new conversation.
   *
   * 1. If a conversation is already open, simply return.
   * 2. Generate a new unique conversation-id and initialize in-memory.
   * 3. Makes a request to Create Conversation endpoint.
   * 4. Updates the conversation status internally to OPENED, for the associated components to reactively update.
   * 5. Performs a cleanup - clears messaging data and closes the Messaging Window, if the request is unsuccessful.
   * @returns {Promise}
   */
  const handleCreateNewConversation = useCallback(
    (routingAttributes?: RoutingAttributes) => {
      if (
        conversationStatus ===
        CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION
      ) {
        sendErrorToSentry({
          additionalContext: { chatId: getConversationId() },
          conversationId: conversationId,
          error:
            'Cannot create a new conversation while a conversation is currently open.',
        });
        return Promise.reject(
          new Error(
            'Salesforce One Chat: Cannot create a new conversation while a conversation is currently open.',
          ),
        );
      }

      // Initialize a new unique conversation-id in-memory.
      storeConversationId(uuidv4());
      return createConversation(getConversationId(), routingAttributes)
        .then(() => {
          const sfConversationId = getConversationId();
          console.log(
            `Successfully created a new conversation with conversation-id: ${sfConversationId}`,
          );
          updateConversationStatus(
            CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION,
          );
          dispatch(setLiveChatStatus('pending'));
          dispatch(
            updateForethoughtLiveChatHelpdeskConversationId(sfConversationId),
          );
          dispatch(
            sendTrackingEvent({
              description: `Created new Salesforce Messaging One Chat conversation ${sfConversationId}`,
              event_type: 'widget-salesforce-event',
              integration: 'salesforce',
            }),
          );
        })
        .catch((err: unknown) => {
          sendErrorToSentry({
            additionalContext: { chatId: getConversationId() },
            conversationId: conversationId,
            error: `Salesforce One Chat handoff error: Something went wrong in creating a new conversation with conversation-id: ${getConversationId()}: ${getErrorMessage(err)}`,
          });
          handleMessagingErrors(err);
          cleanupMessagingData();
          handleLiveChatFailure({
            error: `Salesforce One Chat handoff error: Something went wrong in creating a new conversation with conversation-id: ${getConversationId()}: ${getErrorMessage(err)}`,
            integration: 'salesforce',
          });
        });
    },
    [
      cleanupMessagingData,
      conversationId,
      conversationStatus,
      dispatch,
      handleLiveChatFailure,
      handleMessagingErrors,
      updateConversationStatus,
    ],
  );

  /**
   * Handles fetching a Continuation Access Token i.e. Messaging JWT.
   *
   * 1. Makes a request to Continuation Access Token endpoint.
   * 2. Updates the web storage with the latest JWT.
   * 3. Performs a cleanup - clears messaging data and closes the Messaging Window, if the request is unsuccessful.
   * @returns {Promise}
   */
  const handleGetContinuityJwt = useCallback(() => {
    return getContinuityJwt()
      .then(response => {
        setJwt(response.accessToken);
      })
      .catch((err: unknown) => {
        sendErrorToSentry({
          additionalContext: { chatId: getConversationId() },
          conversationId: conversationId,
          error: `Salesforce One Chat: Something went wrong in fetching a Continuation Access Token: ${getErrorMessage(err)}`,
        });
        handleMessagingErrors(err);
        throw new Error('Failed to fetch a Continuation access token.');
      });
  }, [conversationId, handleMessagingErrors]);

  /**
   * Adds a Conversation Entry object to the list of conversation entries. Updates the state of the list of conversation entries for the component(s) to be updated in-turn, reactively.
   * @param {object} conversationEntry - entry object for the current conversation.
   */
  const addConversationEntry = useCallback(
    (conversationEntry: ConversationEntryUtil.AppConversationEntry) => {
      conversationEntries.push(conversationEntry);
      setConversationEntries([...conversationEntries]);
    },
    [conversationEntries],
  );

  /**
   * Generate a Conversation Entry object from the server sent event.
   *
   * 1. Create a Conversation Entry object from the parsed event data.
   * 2. Return the Conversation Entry if the conversationEntry is for the current conversation and undefined, otherwise.
   * @param {object} event - Event data payload from server-sent event.
   * @returns {object|undefined}
   */
  const generateConversationEntryForCurrentConversation = useCallback(
    (parsedEventData: ServerSentEvent) => {
      const conversationEntry = ConversationEntryUtil.createConversationEntry(
        parsedEventData,
        conversationId,
        (
          liveChatWidgetComponent?.component_fields
            .integration_config as SalesforceMessagingLiveChatIntegrationConfig
        ).integration_identifier,
      );

      // Handle server sent events only for the current conversation
      if (parsedEventData.conversationId === getConversationId()) {
        return conversationEntry;
      }
      console.log(
        `Current conversation-id: ${getConversationId()} does not match the conversation-id in server sent event: ${parsedEventData.conversationId}. Ignoring the event.`,
      );
      return undefined;
    },
    [
      conversationId,
      liveChatWidgetComponent?.component_fields.integration_config,
    ],
  );

  /**
   * Helper function to be used by `handleConversationMessageServerSentEvent`
   * and `handleListConversationEntries` to restore missed messages.
   */
  const handleMessageConversationEntry = useCallback(
    ({
      conversationEntry,
      sendReadReceipt = true,
    }: {
      conversationEntry: ReturnType<
        typeof generateConversationEntryForCurrentConversation
      >;
      sendReadReceipt?: boolean;
    }) => {
      function getFormFields({ input }: Section) {
        function determineInputType(
          input: TextInput,
        ): FormFields['input_type'] {
          if (input.keyboardType === 'DecimalPad') {
            return 'NUMBER';
          }

          if (
            input.textContentType === 'TelephoneNumber' ||
            input.keyboardType === 'PhonePad'
          ) {
            return 'PHONE_NUMBER';
          }

          if (
            input.textContentType === 'EmailAddress' ||
            input.keyboardType === 'EmailAddress'
          ) {
            return 'EMAIL';
          }

          if (input.textInputType === 'Multiline') {
            return 'LONG_TEXT';
          }

          return 'SHORT_TEXT';
        }

        if (input.inputType === 'TextInput') {
          return {
            configuration_fields: {
              max_length: input.maximumCharacterCount,
            },
            input_type: determineInputType(input),
            output_variable: input.id,
            placeholder: input.label.title,
          } as const satisfies FormFields;
        } else if (input.inputType === 'DatePickerInput') {
          return {
            input_type: 'DATE',
            output_variable: input.id,
            placeholder: input.label.title,
          } as const satisfies FormFields;
        } else if (input.inputType === 'SelectInput') {
          return {
            input_type: input.multipleSelection ? 'MULTI_SELECT_LIST' : 'LIST',
            list_type_options: input.optionItems.map(option => ({
              label: option.titleItem.title,
              value: option.optionIdentifier,
            })),
            output_variable: input.id,
            placeholder: input.label.title,
          } as const satisfies FormFields;
        }

        // Fall back to text input:
        return {
          input_type: 'SHORT_TEXT',
          output_variable: (input as TextInput).id || '',
          placeholder: (input as TextInput).label.title,
        } as const satisfies FormFields;
      }

      if (!conversationEntry) {
        return;
      }

      try {
        if (ConversationEntryUtil.isMessageFromEndUser(conversationEntry)) {
          conversationEntry.isEndUserMessage = true;

          // Since message is echoed back by the server, mark the conversation entry as sent.
          conversationEntry.isSent = true;

          console.log('End user successfully sent a message.');
        } else {
          conversationEntry.isEndUserMessage = false;
          console.log(
            `Successfully received a message from ${conversationEntry.actorType}`,
          );

          if (ConversationEntryUtil.isTextMessage(conversationEntry)) {
            dispatch(
              addLiveChatWidgetComponents({
                components: [
                  {
                    component_fields: {
                      agentName: conversationEntry.actorName,
                      author: conversationEntry.actorName,
                      message:
                        ConversationEntryUtil.getTextMessageContent(
                          conversationEntry,
                        ),

                      // Markdown is not supported by Salesforce Messaging, but
                      // `should_render_as_markdown: true` is needed for
                      // rendering newline characters:
                      should_render_as_markdown: true,

                      should_render_single_new_line_characters: true,
                    },
                    component_type: 'text_message',
                    isInAgentConvo: true,
                    isSent: true,
                    timestamp: conversationEntry.transcriptedTimestamp,
                  } satisfies UserMessageComponent,
                ],
                helpdeskConversationId: conversationEntry.conversationId,
                sendAgentMessage: true,
              }),
            );
          } else if (
            ConversationEntryUtil.isAttachmentsMessage(conversationEntry)
          ) {
            dispatch(
              addLiveChatWidgetComponents({
                components: [
                  {
                    component_fields: {
                      agentName: conversationEntry.actorName,
                      attachments:
                        conversationEntry.content.staticContent.attachments.map(
                          ({ mimeType, name, url }) => ({
                            content_type: mimeType,
                            file_extension: mimeType.split('/').pop(),
                            name: name,
                            url: url,
                          }),
                        ),
                      author: conversationEntry.actorName,
                      message:
                        conversationEntry.content.staticContent.text || '',
                    },
                    component_type: 'text_message',
                    isInAgentConvo: true,
                    isSent: true,
                    timestamp: conversationEntry.transcriptedTimestamp,
                  } satisfies UserMessageComponent,
                ],
              }),
            );
          } else if (
            ConversationEntryUtil.isRichLinkMessage(conversationEntry)
          ) {
            dispatch(
              addLiveChatWidgetComponents({
                components: [
                  {
                    component_fields: {
                      agentName: conversationEntry.actorName,
                      author: conversationEntry.actorName,
                      message: '',
                      metadata: {
                        description:
                          conversationEntry.content.staticContent.linkItem
                            .titleItem.subTitle ||
                          conversationEntry.content.staticContent.linkItem
                            .titleItem.secondarySubTitle ||
                          conversationEntry.content.staticContent.linkItem
                            .titleItem.tertiarySubTitle ||
                          conversationEntry.content.staticContent.linkItem
                            .titleItem.imageSubTitle,
                        image:
                          conversationEntry.content.staticContent.image
                            .assetUrl,
                        title:
                          conversationEntry.content.staticContent.linkItem
                            .titleItem.title,
                        url: conversationEntry.content.staticContent.linkItem
                          .url,
                      },
                    },
                    component_type: 'text_message',
                    isInAgentConvo: true,
                    isSent: true,
                    timestamp: conversationEntry.transcriptedTimestamp,
                  } satisfies UserMessageComponent,
                ],
              }),
            );
          } else if (
            ConversationEntryUtil.isChoicesMessage(conversationEntry)
          ) {
            if (
              'choices' in conversationEntry.content &&
              conversationEntry.content.choices
            ) {
              dispatch(
                addLiveChatWidgetComponents({
                  components: [
                    {
                      component_fields: {
                        agentName: conversationEntry.actorName,
                        author: conversationEntry.actorName,
                        multi_select: false,
                        options:
                          conversationEntry.content.choices.optionItems.map(
                            option => ({
                              display_name: option.titleItem.title,
                              value: option.optionIdentifier,
                            }),
                          ),
                        output_variable: '',
                        prompt: conversationEntry.content.choices.text,
                        quotedMessageId:
                          conversationEntry.messageId ?? undefined,
                        should_render_as_markdown: true,
                        should_render_single_new_line_characters: true,
                      },
                      component_type: 'buttons',
                      isInAgentConvo: true,
                      timestamp: conversationEntry.transcriptedTimestamp,
                    } satisfies ButtonComponent,
                  ],
                }),
              );
            }
          } else if (conversationEntry.messageType === 'FormMessage') {
            const castedConversationEntry =
              conversationEntry as unknown as SalesforceFormMessageComponent;
            dispatch(
              addLiveChatWidgetComponents({
                components: [
                  {
                    component_fields: {
                      agentName: castedConversationEntry.actorName,
                      forethought_text: {
                        checkbox_no: 'No',
                        checkbox_placeholder: 'Select one',
                        checkbox_yes: 'Yes',
                        dropdown_back: 'Back',
                        email_error: 'Please enter a valid email',
                        never_mind: 'Never mind',
                        number_error: 'Please enter a valid number',
                        phone_error: 'Please enter a valid phone number',
                        required_error: 'Field is required',
                        required_label: 'Required',
                        short_text_alphanumeric_error:
                          'Text must be alphanumeric',
                        short_text_equal_error:
                          'Text must be exactly %s characters',
                        short_text_max_error:
                          'Text cannot go over %s characters',
                        short_text_min_error:
                          'Text needs to be at least %s characters',
                        submit_button: 'Send',
                      },
                      form_fields:
                        castedConversationEntry.content.form.sections.map(
                          getFormFields,
                        ),
                      prompt:
                        castedConversationEntry.content.form.message.title ||
                        castedConversationEntry.content.form.formTitle.title ||
                        '',
                      quotedMessageId:
                        castedConversationEntry.messageId ?? undefined,

                      // Markdown is not supported by Salesforce Messaging, but
                      // `should_render_as_markdown: true` is needed for
                      // rendering newline characters:
                      should_render_as_markdown: true,

                      should_render_single_new_line_characters: true,
                    },
                    component_type: 'form',
                    isInAgentConvo: true,
                    timestamp: castedConversationEntry.transcriptedTimestamp,
                  } satisfies FormStepComponent,
                ],
              }),
            );
          } else {
            dispatch(
              addLiveChatWidgetComponents({
                components: [
                  {
                    component_fields: {
                      agentName: conversationEntry.actorName,
                      author: conversationEntry.actorName,
                      message: '[The agent sent a message of unsupported type]',
                    },
                    component_type: 'text_message',
                    isInAgentConvo: true,
                    isSent: true,
                    timestamp: conversationEntry.transcriptedTimestamp,
                  } satisfies UserMessageComponent,
                ],
                helpdeskConversationId: conversationEntry.conversationId,
                sendAgentMessage: true,
              }),
            );
            captureMessage('Unsupported message type Salesforce Live Chat', {
              extra: {
                conversationId,
                integrationId: (
                  liveChatWidgetComponent?.component_fields
                    .integration_config as SalesforceMessagingLiveChatIntegrationConfig
                ).integration_identifier,
                message: conversationEntry,
                origin: ApiService.headers['solve-origin'],
              },
              level: 'info',
            });
          }

          if (sendReadReceipt) {
            sendMessageReadReceipt({
              conversationEntryId: conversationEntry.messageId,
              conversationId: getConversationId(),
            })
              .then(() => {
                console.log(
                  `Successfully sent read receipt for conversation entry: ${conversationEntry.messageId}`,
                );
              })
              .catch((err: unknown) => {
                sendErrorToSentry({
                  additionalContext: { chatId: getConversationId() },
                  conversationId: conversationId,
                  error: `Salesforce One Chat: Something went wrong in sending read receipt for conversation entry: ${conversationEntry.messageId}: ${getErrorMessage(
                    err,
                  )}`,
                });
              });
          }
        }
        addConversationEntry(conversationEntry);
      } catch (err) {
        sendErrorToSentry({
          additionalContext: { chatId: getConversationId() },
          conversationId: conversationId,
          error: `Salesforce One Chat: Something went wrong in handling conversation message server sent event: ${getErrorMessage(err)}`,
        });
      }
    },
    [
      addConversationEntry,
      conversationId,
      dispatch,
      liveChatWidgetComponent?.component_fields.integration_config,
    ],
  );

  /**
   * Handles fetching a list of all conversation entries for the current conversation.
   *
   * 1. Makes a request to List Conversation Entries endpoint.
   * 2. Renders the conversation entries based on their Entry Type.
   * @returns {Promise}
   */
  const handleListConversationEntries = useCallback(() => {
    return listConversationEntries(getConversationId())
      .then(response => {
        if (Array.isArray(response)) {
          [...response].reverse().forEach((entry, idx, arr) => {
            const conversationEntry =
              generateConversationEntryForCurrentConversation(entry);
            if (!conversationEntry) {
              return;
            }

            switch (conversationEntry.entryType) {
              case CONVERSATION_CONSTANTS.EntryTypes.CONVERSATION_MESSAGE:
                conversationEntry.isEndUserMessage =
                  ConversationEntryUtil.isMessageFromEndUser(conversationEntry);
                // This will add any missing messages to the widget components
                handleMessageConversationEntry({
                  conversationEntry,
                  sendReadReceipt: idx === arr.length - 1,
                });
                break;
              case CONVERSATION_CONSTANTS.EntryTypes.PARTICIPANT_CHANGED:
              case CONVERSATION_CONSTANTS.EntryTypes.ROUTING_RESULT:
                addConversationEntry(conversationEntry);
                break;
              default:
                console.log(
                  `Unrecognized conversation entry type: ${conversationEntry.entryType}.`,
                );
            }
          });
        } else {
          sendErrorToSentry({
            additionalContext: { chatId: getConversationId() },
            conversationId: conversationId,
            error: `Salesforce One Chat: Expecting a response of type Array from listConversationEntries but instead received: ${response}`,
          });
        }
      })
      .catch((err: unknown) => {
        sendErrorToSentry({
          additionalContext: { chatId: getConversationId() },
          conversationId: conversationId,
          error: `Salesforce One Chat: Something went wrong while processing entries from listConversationEntries response: ${getErrorMessage(err)}`,
        });
        handleMessagingErrors(err);
        throw new Error(
          'Failed to list the conversation entries for the current conversation.',
        );
      });
  }, [
    addConversationEntry,
    conversationId,
    generateConversationEntryForCurrentConversation,
    handleMessageConversationEntry,
    handleMessagingErrors,
  ]);

  /**
   * Handles fetching a list of all conversations available. This returns only conversations which are OPEN, unless otherwise specified in the request.
   *
   * 1. Makes a request to List Conversations endpoint.
   * 2. If there are multiple OPEN conversations, loads the conversation with the most-recent start time.
   * 3. Performs a cleanup - clears messaging data and closes the Messaging Window, if the request is unsuccessful.
   * @returns {Promise}
   */
  const handleListConversations = useCallback(() => {
    return listConversations()
      .then(response => {
        if (
          response &&
          response.openConversationsFound > 0 &&
          response.conversations.length
        ) {
          const openConversations = response.conversations;
          if (openConversations.length > 1) {
            console.warn(
              `Expected the user to be participating in 1 open conversation but instead found ${openConversations.length}. Loading the conversation with latest startTimestamp.`,
            );
            openConversations.sort(
              (conversationA, conversationB) =>
                conversationB.startTimestamp - conversationA.startTimestamp,
            );
          }
          // Update conversation-id with the one from service.
          storeConversationId(openConversations[0]?.conversationId || '');
          updateConversationStatus(
            CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION,
          );
        } else {
          // No open conversations found.
          cleanupMessagingData();
          handleLiveChatFailure({
            error:
              'Salesforce One Chat handoff error: No open conversations found',
            integration: 'salesforce',
          });
        }
      })
      .catch((err: unknown) => {
        sendErrorToSentry({
          additionalContext: { chatId: getConversationId() },
          conversationId: conversationId,
          error: `Salesforce One Chat: Something went wrong in fetching a list of conversations: ${getErrorMessage(err)}`,
        });
        handleMessagingErrors(err);
        throw new Error('Failed to list the conversations.');
      });
  }, [
    cleanupMessagingData,
    conversationId,
    handleLiveChatFailure,
    handleMessagingErrors,
    updateConversationStatus,
  ]);

  /**
   * Handles an existing conversation.
   *
   * 1. Fetch a Continuation Access Token i.e. Messaging JWT.
   * 2. Lists the available conversations and loads the current (also most-recent) conversation that is OPEN.
   * 3. Fetch the entries for the current conversation.
   * @returns {Promise}
   */
  const handleExistingConversation = useCallback(() => {
    return handleGetContinuityJwt()
      .then(() => {
        return handleListConversations()
          .then(() => {
            console.log('Successfully listed the conversations.');
            handleListConversationEntries()
              .then(() =>
                console.log(
                  `Successfully retrieved entries for the current conversation: ${getConversationId()}`,
                ),
              )
              .catch((err: unknown) => {
                sendErrorToSentry({
                  additionalContext: { chatId: getConversationId() },
                  conversationId: conversationId,
                  error: `Salesforce One Chat: ${getErrorMessage(err)}`,
                });
              });
          })
          .catch((err: unknown) => {
            sendErrorToSentry({
              additionalContext: { chatId: getConversationId() },
              conversationId: conversationId,
              error: `Salesforce One Chat: ${getErrorMessage(err)}`,
            });
          });
      })
      .catch((err: unknown) => {
        sendErrorToSentry({
          additionalContext: { chatId: getConversationId() },
          conversationId: conversationId,
          error: `Salesforce One Chat: ${getErrorMessage(err)}`,
        });
      });
  }, [
    conversationId,
    handleGetContinuityJwt,
    handleListConversationEntries,
    handleListConversations,
  ]);

  /**
   * Handles fetching an Access Token i.e. Messaging JWT.
   *
   * 1. If a JWT already exists, simply return.
   * 2. Makes a request to Access Token endpoint.
   * 3. Updates the web storage with the latest JWT.
   * 4. Performs a cleanup - clears messaging data and closes the Messaging Window, if the request is unsuccessful.
   * @returns {Promise}
   */
  const handleGetJwt = useCallback(() => {
    if (getJwt()) {
      console.warn(
        'Messaging access token (JWT) already exists in the web storage. Discontinuing to create a new access token.',
      );
      return handleExistingConversation().then(Promise.reject);
    }

    const { jwt } = (
      liveChatWidgetComponent?.component_fields
        .integration_config as SalesforceMessagingLiveChatIntegrationConfig
    ).integration_fields;

    const accessTokenType = jwt ? 'authenticated' : 'unauthenticated';

    const accessTokenPromise = jwt
      ? getAuthenticatedAccessToken(jwt)
      : getUnauthenticatedAccessToken();

    return accessTokenPromise
      .then(response => {
        console.log(`Successfully fetched an ${accessTokenType} access token.`);
        // Parse the response object which includes access-token (JWT), configutation data.
        if (typeof response === 'object') {
          setJwt(response.accessToken);
          setLastEventId(response.lastEventId);
          const deploymentConfiguration =
            response.context &&
            response.context.configuration &&
            response.context.configuration.embeddedServiceConfig;
          setDeploymentConfiguration(deploymentConfiguration);
          // Need to also store it in Redux so that it would persist after page refresh:
          dispatch(setDeploymentConfigurationAction(deploymentConfiguration));
        }
      })
      .catch((err: unknown) => {
        sendErrorToSentry({
          additionalContext: { chatId: getConversationId() },
          conversationId: conversationId,
          error: `Salesforce One Chat handoff error: Something went wrong in fetching an ${accessTokenType} access token: ${getErrorMessage(err)}`,
        });
        handleMessagingErrors(err);
        cleanupMessagingData();
        handleLiveChatFailure({
          error: `Salesforce One Chat handoff error: Something went wrong in fetching an ${accessTokenType} access token: ${getErrorMessage(err)}`,
          integration: 'salesforce',
        });
      });
  }, [
    cleanupMessagingData,
    conversationId,
    dispatch,
    handleExistingConversation,
    handleLiveChatFailure,
    handleMessagingErrors,
    liveChatWidgetComponent?.component_fields.integration_config,
  ]);

  /**
   * Handles a new conversation.
   *
   * 1. Fetch an Access Token i.e. Messaging JWT.
   * 2. Create a new conversation.
   * @returns {Promise}
   */
  const handleNewConversation = useCallback(() => {
    return handleGetJwt()
      .then(() => {
        if (prechatUtil.shouldDisplayPrechatForm()) {
          // TODO: Implement pre-chat form rendering
          console.warn(
            'Pre-Chat is enabled but not supported. Continuing to create a new conversation.',
          );
          setShowPrechatForm(true);
        } else {
          console.log(
            'Pre-Chat is not enabled. Continuing to create a new conversation.',
          );
        }

        return handleCreateNewConversation({
          ...(
            liveChatWidgetComponent?.component_fields
              .integration_config as SalesforceMessagingLiveChatIntegrationConfig
          ).integration_fields.hidden_fields,
          ...(
            liveChatWidgetComponent?.component_fields
              .integration_config as SalesforceMessagingLiveChatIntegrationConfig
          ).integration_fields.visible_fields,
        })
          .then(() => {
            const integrationConfig = liveChatWidgetComponent?.component_fields
              .integration_config as SalesforceMessagingLiveChatIntegrationConfig;
            const sfConversationId = getConversationId();

            console.log(
              `Completed initializing a new conversation with conversationId: ${sfConversationId}`,
            );

            if (integrationConfig?.integration_fields?.question) {
              sendTextMessage(
                sfConversationId,
                integrationConfig.integration_fields.question,
                uuidv4(),
              )
                .then(() => {
                  console.log(
                    `Successfully sent transcript to conversation: ${sfConversationId}`,
                  );
                })
                .catch((err: unknown) => {
                  sendErrorToSentry({
                    additionalContext: { chatId: sfConversationId },
                    conversationId: conversationId,
                    error: `Failed to send transcript to conversation: ${sfConversationId}: ${getErrorMessage(err)}`,
                  });
                });
            }
          })
          .catch((err: unknown) => {
            sendErrorToSentry({
              additionalContext: { chatId: getConversationId() },
              conversationId: conversationId,
              error: `Salesforce One Chat: ${getErrorMessage(err)}`,
            });
          });
      })
      .catch((err: unknown) => {
        sendErrorToSentry({
          additionalContext: { chatId: getConversationId() },
          conversationId: conversationId,
          error: `Salesforce One Chat: ${getErrorMessage(err)}`,
        });
      });
  }, [
    conversationId,
    handleCreateNewConversation,
    handleGetJwt,
    liveChatWidgetComponent?.component_fields.integration_config,
  ]);

  /**
   * Handle a CONVERSATION_MESSAGE server-sent event.
   *
   * This includes:
   *  1. Parse, populate, and create ConversationEntry object based on its entry type
   *      NOTE: Skip processing CONVERSATION_MESSAGE if the newly created ConversationEntry is undefined or invalid or not from the current conversation.
   *  2. Updates in-memory list of conversation entries and the updated list gets reactively passed on to MessagingBody.
   * @param {object} event - Event data payload from server-sent event.
   */
  const handleConversationMessageServerSentEvent = useCallback(
    (event: MessageEvent) => {
      console.log(
        'Successfully handling conversation message server sent event.',
        event,
      );
      // Update in-memory to the latest lastEventId
      if (event && event.lastEventId) {
        setLastEventId(event.lastEventId);
      }

      try {
        const parsedEventData =
          ConversationEntryUtil.parseServerSentEventData(event);
        const conversationEntry =
          generateConversationEntryForCurrentConversation(parsedEventData);
        handleMessageConversationEntry({ conversationEntry });
      } catch (err) {
        sendErrorToSentry({
          additionalContext: { chatId: getConversationId() },
          conversationId: conversationId,
          error: `Salesforce One Chat: Something went wrong in handling conversation message server sent event: ${getErrorMessage(err)}`,
        });
      }
    },
    [
      conversationId,
      generateConversationEntryForCurrentConversation,
      handleMessageConversationEntry,
    ],
  );

  /**
   * Handle a ROUTING_RESULT server-sent event.
   *
   * This includes:
   *  1. Parse, populate, and create ConversationEntry object based on its entry type.
   *      NOTE: Skip processing ROUTING_RESULT if the newly created ConversationEntry is undefined or invalid or not from the current conversation.
   *  2. Updates in-memory list of conversation entries and the updated list gets reactively passed on to MessagingBody.
   *
   *  NOTE: Update the chat client based on the latest routing result. E.g. if the routing type is transfer, set an internal flag like `isTransferring` to 'true' and use that to show a transferring indicator in the ui.
   * @param {object} event - Event data payload from server-sent event.
   */
  const handleRoutingResultServerSentEvent = useCallback(
    (event: MessageEvent) => {
      try {
        console.log(
          'Successfully handling routing result server sent event.',
          event,
        );
        // Update in-memory to the latest lastEventId
        if (event && event.lastEventId) {
          setLastEventId(event.lastEventId);
        }

        const parsedEventData =
          ConversationEntryUtil.parseServerSentEventData(event);
        const conversationEntry =
          generateConversationEntryForCurrentConversation(parsedEventData);
        if (!conversationEntry) {
          return;
        }

        // TODO: Should use type predicate here instead of `as`:
        const content = conversationEntry.content as ParsedRoutingEntryPayload;

        if (
          conversationEntry.messageType ===
          CONVERSATION_CONSTANTS.RoutingTypes.INITIAL
        ) {
          dispatch(setLiveChatStatus('pending'));

          // Render reasonForNotRouting when initial routing fails.
          switch (content.failureType) {
            case CONVERSATION_CONSTANTS.RoutingFailureTypes.NO_ERROR:
            case CONVERSATION_CONSTANTS.RoutingFailureTypes.SUBMISSION_ERROR:
            case CONVERSATION_CONSTANTS.RoutingFailureTypes.ROUTING_ERROR:
            case CONVERSATION_CONSTANTS.RoutingFailureTypes.UNKNOWN_ERROR:
              addConversationEntry(conversationEntry);
              break;
            default:
              sendErrorToSentry({
                additionalContext: { chatId: getConversationId() },
                conversationId: conversationId,
                error: `Salesforce One Chat: Unrecognized initial routing failure type: ${content.failureType}`,
              });
          }
          // Handle when a conversation is being transferred.
        } else if (
          conversationEntry.messageType ===
          CONVERSATION_CONSTANTS.RoutingTypes.TRANSFER
        ) {
          switch (content.failureType) {
            case CONVERSATION_CONSTANTS.RoutingFailureTypes.NO_ERROR:
              // Render transfer timestamp when transfer is requested successfully.
              // TODO: Add a transfer state ui update.
              addConversationEntry(conversationEntry);
              break;
            case CONVERSATION_CONSTANTS.RoutingFailureTypes.SUBMISSION_ERROR:
            case CONVERSATION_CONSTANTS.RoutingFailureTypes.ROUTING_ERROR:
            case CONVERSATION_CONSTANTS.RoutingFailureTypes.UNKNOWN_ERROR:
              break;
            default:
              sendErrorToSentry({
                additionalContext: { chatId: getConversationId() },
                conversationId: conversationId,
                error: `Salesforce One Chat: Unrecognized transfer routing failure type: ${content.failureType}`,
              });
          }
        } else {
          sendErrorToSentry({
            additionalContext: { chatId: getConversationId() },
            conversationId: conversationId,
            error: `Salesforce One Chat: Unrecognized routing type: ${conversationEntry.messageType}`,
          });
        }
      } catch (err) {
        sendErrorToSentry({
          additionalContext: { chatId: getConversationId() },
          conversationId: conversationId,
          error: `Salesforce One Chat: Something went wrong in handling routing result server sent event: ${getErrorMessage(err)}`,
        });
      }
    },
    [
      addConversationEntry,
      conversationId,
      dispatch,
      generateConversationEntryForCurrentConversation,
    ],
  );

  /**
   * Handle a PARTICIPANT_CHANGED server-sent event.
   *
   * This includes:
   *  1. Parse, populate, and create ConversationEntry object based on its entry type.
   *      NOTE: Skip processing PARTICIPANT_CHANGED if the newly created ConversationEntry is undefined or invalid or not from the current conversation.
   *  2. Updates in-memory list of conversation entries and the updated list gets reactively passed on to MessagingBody.
   * @param {object} event - Event data payload from server-sent event.
   */
  const handleParticipantChangedServerSentEvent = useCallback(
    (event: MessageEvent) => {
      try {
        console.log(
          'Successfully handling participant changed server sent event.',
          event,
        );
        // Update in-memory to the latest lastEventId
        if (event && event.lastEventId) {
          setLastEventId(event.lastEventId);
        }

        const parsedEventData =
          ConversationEntryUtil.parseServerSentEventData(event);
        const conversationEntry =
          generateConversationEntryForCurrentConversation(parsedEventData);
        if (!conversationEntry) {
          return;
        }
        addConversationEntry(conversationEntry);

        const content =
          conversationEntry.content as ParsedParticipantChangedEntryPayload;
        const participantEntry = content.entries[0];

        // Only handle Agent participant changes.
        if (participantEntry && participantEntry.participant.role === 'Agent') {
          switch (participantEntry.operation) {
            case 'add':
              dispatch(
                onAgentConnected({ agentName: participantEntry.displayName }),
              );
              dispatch(
                addLiveChatEventMessage({
                  message: `${participantEntry.displayName} has joined the chat`,
                  timestamp: conversationEntry.transcriptedTimestamp,
                }),
              );
              break;
            case 'remove':
              dispatch(
                addLiveChatEventMessage({
                  message: `${participantEntry.displayName} has left the chat`,
                  timestamp: conversationEntry.transcriptedTimestamp,
                }),
              );

              if (strictEndChatDetection) {
                // detect if conversation was ended or not
                getConversationRoutingStatus({
                  conversationId: getConversationId(),
                })
                  .then(res => {
                    if (res.routingStatus === 'NEEDS_ROUTING') {
                      endConversation();
                    }
                  })
                  .catch((err: unknown) => {
                    throw err;
                  });
              }
              break;
          }
        }
      } catch (err) {
        sendErrorToSentry({
          additionalContext: { chatId: getConversationId() },
          conversationId: conversationId,
          error: `Salesforce One Chat: Something went wrong in handling participant changed server sent event: ${getErrorMessage(err)}`,
        });
      }
    },
    [
      addConversationEntry,
      conversationId,
      dispatch,
      endConversation,
      generateConversationEntryForCurrentConversation,
      strictEndChatDetection,
    ],
  );

  /**
   * Handle a TYPING_STARTED_INDICATOR server-sent event.
   * @param {object} event - Event data payload from server-sent event.
   */
  const handleTypingStartedIndicatorServerSentEvent = useCallback(
    (event: MessageEvent) => {
      try {
        // Update in-memory to the latest lastEventId
        if (event && event.lastEventId) {
          setLastEventId(event.lastEventId);
        }

        const parsedEventData =
          ConversationEntryUtil.parseServerSentEventData(event);
        // Handle typing indicators only for the current conversation
        if (getConversationId() === parsedEventData.conversationId) {
          const senderName =
            ConversationEntryUtil.getSenderDisplayName(parsedEventData) ||
            ConversationEntryUtil.getSenderRole(parsedEventData);
          const typingParticipantTimer =
            currentTypingParticipants[senderName] &&
            currentTypingParticipants[senderName].countdownTimer;

          // If we have received typing indicator from this sender within the past 5 seconds, reset the timer
          // Otherwise, start a new timer
          if (
            ConversationEntryUtil.getSenderRole(parsedEventData) !==
            CONVERSATION_CONSTANTS.ParticipantRoles.ENDUSER
          ) {
            console.log(
              'Successfully handling typing started indicator server sent event.',
              event,
            );

            if (typingParticipantTimer) {
              typingParticipantTimer.reset(Date.now());
            } else {
              currentTypingParticipants[senderName] = {
                countdownTimer: new CountdownTimer(
                  () => {
                    delete currentTypingParticipants[senderName];

                    if (!Object.keys(currentTypingParticipants).length) {
                      setIsAnotherParticipantTyping(false);
                    }
                  },
                  CLIENT_CONSTANTS.TYPING_INDICATOR_DISPLAY_TIMEOUT,
                  Date.now(),
                ),
                role: parsedEventData.conversationEntry.sender.role,
              };

              currentTypingParticipants[senderName].countdownTimer.start();
            }

            setIsAnotherParticipantTyping(true);
          }
        }
      } catch (err) {
        sendErrorToSentry({
          additionalContext: { chatId: getConversationId() },
          conversationId: conversationId,
          error: `Salesforce One Chat: Something went wrong in handling typing started indicator server sent event: ${getErrorMessage(err)}`,
        });
      }
    },
    [conversationId, currentTypingParticipants],
  );

  /**
   * Handle a TYPING_STOPPED_INDICATOR server-sent event.
   * @param {object} event - Event data payload from server-sent event.
   */
  const handleTypingStoppedIndicatorServerSentEvent = useCallback(
    (event: MessageEvent) => {
      try {
        // Update in-memory to the latest lastEventId
        if (event && event.lastEventId) {
          setLastEventId(event.lastEventId);
        }

        const parsedEventData =
          ConversationEntryUtil.parseServerSentEventData(event);
        // Handle typing indicators only for the current conversation
        if (getConversationId() === parsedEventData.conversationId) {
          const senderName =
            ConversationEntryUtil.getSenderDisplayName(parsedEventData) ||
            ConversationEntryUtil.getSenderRole(parsedEventData);

          delete currentTypingParticipants[senderName];

          if (!Object.keys(currentTypingParticipants).length) {
            setIsAnotherParticipantTyping(false);
          }
        }
      } catch (err) {
        sendErrorToSentry({
          additionalContext: { chatId: getConversationId() },
          conversationId: conversationId,
          error: `Salesforce One Chat: Something went wrong in handling typing stopped indicator server sent event: ${getErrorMessage(err)}`,
        });
      }
    },
    [conversationId, currentTypingParticipants],
  );

  /**
   * Handle a server-sent event CONVERSATION_DELIVERY_ACKNOWLEDGEMENT:
   * - Parse server-sent event and update a conversation entry (if it exists) with data from the event.
   * - Store delivery acknowledgement timestamp on conversation entry to show receipt timestamp.
   * @param {object} event - Event data payload from server-sent event.
   */
  const handleConversationDeliveryAcknowledgementServerSentEvent = useCallback(
    (event: MessageEvent) => {
      try {
        console.log(
          'Successfully handling conversation delivery acknowledgement server sent event.',
          event,
        );
        // Update in-memory to the latest lastEventId
        if (event && event.lastEventId) {
          setLastEventId(event.lastEventId);
        }

        const parsedEventData =
          ConversationEntryUtil.parseServerSentEventData(event);
        // Handle delivery acknowledgements only for the current conversation
        if (getConversationId() === parsedEventData.conversationId) {
          if (
            parsedEventData.conversationEntry &&
            parsedEventData.conversationEntry.relatedRecords.length &&
            parsedEventData.conversationEntry.entryPayload.length
          ) {
            // Store acknowledgement timestamp and identifier of message that was delivered.
            const deliveredMessageId =
              parsedEventData.conversationEntry.relatedRecords[0];
            const deliveryAcknowledgementTimestamp = (
              JSON.parse(
                parsedEventData.conversationEntry.entryPayload,
              ) as ParsedDeliveryAcknowledgementEntryPayload
            ).acknowledgementTimestamp;

            // Make deep copy of conversation entries to find matching messageId.
            util
              .createDeepCopy(conversationEntries)
              .filter(conversationEntry => {
                if (
                  conversationEntry &&
                  conversationEntry.messageId === deliveredMessageId
                ) {
                  conversationEntry.isDelivered = true;
                  conversationEntry.deliveryAcknowledgementTimestamp =
                    deliveryAcknowledgementTimestamp;
                }
              });
          }
        }
      } catch (err) {
        sendErrorToSentry({
          additionalContext: { chatId: getConversationId() },
          conversationId: conversationId,
          error: `Salesforce One Chat: Something went wrong in handling conversation delivery acknowledgement server sent event: ${getErrorMessage(err)}`,
        });
      }
    },
    [conversationEntries, conversationId],
  );

  /**
   * Handle a server-sent event CONVERSATION_READ_ACKNOWLEDGEMENT:
   * - Parse server-sent event and update a conversation entry (if it exists) with data from the event.
   * - Store read acknowledgement timestamp on conversation entry to show receipt timestamp.
   * @param {object} event - Event data payload from server-sent event.
   */
  const handleConversationReadAcknowledgementServerSentEvent = useCallback(
    (event: MessageEvent) => {
      try {
        console.log(
          'Successfully handling conversation read acknowledgement server sent event.',
          event,
        );
        // Update in-memory to the latest lastEventId
        if (event && event.lastEventId) {
          setLastEventId(event.lastEventId);
        }

        const parsedEventData =
          ConversationEntryUtil.parseServerSentEventData(event);
        // Handle read acknowledgements only for the current conversation
        if (getConversationId() === parsedEventData.conversationId) {
          if (
            parsedEventData.conversationEntry &&
            parsedEventData.conversationEntry.relatedRecords.length &&
            parsedEventData.conversationEntry.entryPayload.length
          ) {
            // Store acknowledgement timestamp and identifier of message that was read.
            const readMessageId =
              parsedEventData.conversationEntry.relatedRecords[0];
            const readAcknowledgementTimestamp = (
              JSON.parse(
                parsedEventData.conversationEntry.entryPayload,
              ) as ParsedReadAcknowledgementEntryPayload
            ).acknowledgementTimestamp;

            // Make deep copy of conversation entries to find matching messageId.
            util
              .createDeepCopy(conversationEntries)
              .filter(conversationEntry => {
                if (
                  conversationEntry &&
                  conversationEntry.messageId === readMessageId
                ) {
                  conversationEntry.isRead = true;
                  conversationEntry.readAcknowledgementTimestamp =
                    readAcknowledgementTimestamp;
                }
              });
          }
        }
      } catch (err) {
        sendErrorToSentry({
          additionalContext: { chatId: getConversationId() },
          conversationId: conversationId,
          error: `Salesforce One Chat: Something went wrong in handling conversation read acknowledgement server sent event: ${getErrorMessage(err)}`,
        });
      }
    },
    [conversationEntries, conversationId],
  );

  /**
   * Handle a CONVERSATION_CLOSED server-sent event.
   *
   * @param {object} event - Event data payload from server-sent event.
   */
  const handleCloseConversationServerSentEvent = useCallback(
    (event: MessageEvent) => {
      try {
        console.log(
          'Successfully handling close conversation server sent event.',
          event,
        );
        // Update in-memory to the latest lastEventId
        if (event && event.lastEventId) {
          setLastEventId(event.lastEventId);
        }

        const parsedEventData =
          ConversationEntryUtil.parseServerSentEventData(event);

        // Do not render conversation ended text if the conversation entry is not for the current conversation.
        if (getConversationId() === parsedEventData.conversationId) {
          endConversation();
        }
      } catch (err) {
        sendErrorToSentry({
          additionalContext: { chatId: getConversationId() },
          conversationId: conversationId,
          error: `Salesforce One Chat: Something went wrong while handling conversation closed server sent event in conversation ${getConversationId()}: ${getErrorMessage(err)}`,
        });
      }
    },
    [conversationId, endConversation],
  );

  /**
   * Handle sending a static text message in a conversation.
   * @param {string} conversationId - Identifier of the conversation.
   * @param {string} value - Actual text of the message.
   * @param {string} messageId - Unique identifier of the message.
   * @param {string} inReplyToMessageId - Identifier of another message where this message is being replied to.
   * @param {boolean} isNewMessagingSession - Whether it is a new messaging session.
   * @param {object} routingAttributes - Pre-Chat fields and its values, if configured.
   * @param {object} language - language code.
   *
   * @returns {Promise}
   */
  const handleSendTextMessage = useCallback(
    (
      conversationId: string,
      value: string,
      messageId: string,
      inReplyToMessageId: string,
      isNewMessagingSession: boolean,
      routingAttributes: RoutingAttributes,
      language: string,
    ) => {
      return sendTextMessage(
        conversationId,
        value,
        messageId,
        inReplyToMessageId,
        isNewMessagingSession,
        routingAttributes,
        language,
      ).catch((err: unknown) => {
        sendErrorToSentry({
          additionalContext: { chatId: getConversationId() },
          conversationId: conversationId,
          error: `Salesforce One Chat: Something went wrong while sending a message to conversation ${conversationId}: ${getErrorMessage(err)}`,
        });
        setFailedMessage({
          inReplyToMessageId,
          isNewMessagingSession,
          language,
          messageId,
          routingAttributes,
          value,
        });
        handleMessagingErrors(err);
      });
    },
    [handleMessagingErrors],
  );

  /**
   * Handles submitting a Pre-Chat form to either start a new conversation or a new messaging session.
   * @param {object} prechatData - Object containing key-value pairs of Pre-Chat form fields and their corresponding values.
   */
  // eslint-disable-next-line unused-imports/no-unused-vars -- TODO @Evgeny will be needed later
  const handlePrechatSubmit = useCallback(
    (prechatData: RoutingAttributes) => {
      let prechatSubmitPromise;
      console.log(`Pre-Chat fields values on submit: ${prechatData}.`);

      // If there is a failed message being tracked while submitting Pre-Chat form, consider it is an existing conversation but a new messaging session. Resend the failed message with the routing attributes to begin a new messaging session.
      if (failedMessage) {
        prechatSubmitPromise = handleSendTextMessage(
          getConversationId(),
          failedMessage.value,
          failedMessage.messageId,
          failedMessage.inReplyToMessageId,
          true,
          prechatData,
          failedMessage.language,
        );
      } else {
        // If there is no failed message being tracked while submitting Pre-Chat form, create a new conversation.
        prechatSubmitPromise = handleCreateNewConversation(prechatData);
      }
      prechatSubmitPromise.then(() => {
        setShowPrechatForm(false);
      });
    },
    [failedMessage, handleCreateNewConversation, handleSendTextMessage],
  );

  /**
   * Used to detect RoutingWorkResult conversation entry to determine if the conversation is closed by agent.
   *
   * @param {object} event - Event data payload from server-sent event.
   */
  const handleRoutingWorkResultServerSentEvent = useCallback(
    (event: MessageEvent) => {
      try {
        console.log(
          'Successfully handling routing work result server sent event.',
          event,
        );
        // Update in-memory to the latest lastEventId
        if (event && event.lastEventId) {
          setLastEventId(event.lastEventId);
        }
        const parsedEventData =
          ConversationEntryUtil.parseServerSentEventData(event);
        const conversationEntry =
          generateConversationEntryForCurrentConversation(parsedEventData);
        if (!conversationEntry) {
          return;
        }

        const content =
          conversationEntry.content as ParsedRoutingWorkResultEntryPayload;

        if (content.routingType === 'Transfer') {
          // do nothing
        } else if (content.workType === 'Closed') {
          // do nothing
        }
      } catch (err) {
        sendErrorToSentry({
          additionalContext: { chatId: getConversationId() },
          conversationId: conversationId,
          error: `Salesforce One Chat: Something went wrong in handling routing work result server sent event: ${getErrorMessage(err)}`,
        });
      }
    },
    [conversationId, generateConversationEntryForCurrentConversation],
  );

  /**
   * Handles establishing a connection to the EventSource i.e. SSE.
   * Selectively listens to the supported events in the app by adding the corresponding event listeners.
   * Note: Update the list of events/event-listeners to add/remove support for the available events. Refer https://developer.salesforce.com/docs/service/messaging-api/references/about/server-sent-events-structure.html
   * @returns {Promise}
   */
  const handleSubscribeToEventSource = useCallback(() => {
    return subscribeToEventSource({
      [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_MESSAGE]:
        handleConversationMessageServerSentEvent,
      [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_ROUTING_RESULT]:
        handleRoutingResultServerSentEvent,
      [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_PARTICIPANT_CHANGED]:
        handleParticipantChangedServerSentEvent,
      [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_TYPING_STARTED_INDICATOR]:
        handleTypingStartedIndicatorServerSentEvent,
      [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_TYPING_STOPPED_INDICATOR]:
        handleTypingStoppedIndicatorServerSentEvent,
      [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_DELIVERY_ACKNOWLEDGEMENT]:
        handleConversationDeliveryAcknowledgementServerSentEvent,
      [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_READ_ACKNOWLEDGEMENT]:
        handleConversationReadAcknowledgementServerSentEvent,
      [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_CLOSE_CONVERSATION]:
        handleCloseConversationServerSentEvent,
      [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_ROUTING_WORK_RESULT]:
        handleRoutingWorkResultServerSentEvent,
    })
      .then(() => {
        console.log('Subscribed to the Event Source (SSE).');
      })
      .catch((err: unknown) => {
        handleMessagingErrors(err);

        if (err instanceof Error) {
          throw err;
        }

        if (typeof err === 'string') {
          throw new Error(err);
        }

        if (
          err &&
          typeof err === 'object' &&
          'message' in err &&
          typeof err.message === 'string'
        ) {
          throw new Error(err.message);
        }

        throw new Error(
          `Failed to subscribe to the Event Source -- ${getErrorMessage(err)}`,
          {
            cause: err,
          },
        );
      });
  }, [
    handleCloseConversationServerSentEvent,
    handleConversationDeliveryAcknowledgementServerSentEvent,
    handleConversationMessageServerSentEvent,
    handleConversationReadAcknowledgementServerSentEvent,
    handleMessagingErrors,
    handleParticipantChangedServerSentEvent,
    handleRoutingResultServerSentEvent,
    handleRoutingWorkResultServerSentEvent,
    handleTypingStartedIndicatorServerSentEvent,
    handleTypingStoppedIndicatorServerSentEvent,
  ]);

  useEffect(() => {
    let conversationStatePromise: Promise<void> | undefined;

    if (
      isCurrentTabPrimary &&
      liveChatWidgetComponent?.component_fields.integration_config
        .integration === 'salesforce' &&
      // Prevent duplicate event source subscriptions:
      (!eventSource || eventSource.readyState === 2) // 2 === CLOSED
    ) {
      conversationStatePromise = getConversationId()
        ? handleExistingConversation()
        : handleNewConversation();
      conversationStatePromise
        .then(() =>
          handleSubscribeToEventSource().catch((err: unknown) => {
            handleLiveChatFailure({
              error: `Salesforce One Chat handoff error: ${getErrorMessage(err)}`,
              integration: 'salesforce',
            });
          }),
        )
        .catch((err: unknown) => {
          handleLiveChatFailure({
            error: `Salesforce One Chat handoff error: ${getErrorMessage(err)}`,
            integration: 'salesforce',
          });
        });

      isInitialized.current = true;
    }

    return () => {
      if (
        liveChatWidgetComponent?.component_fields.integration_config
          .integration !== 'salesforce' &&
        isInitialized.current
      ) {
        cleanupMessagingData();
      }
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- Adding
      handleExistingConversation, OR handleNewConversation, OR
      handleSubscribeToEventSource causes an infinite loop
    */
  }, [
    liveChatWidgetComponent?.component_fields.integration_config.integration,
    isCurrentTabPrimary,
    dispatch,
    widgetInitMethod,
    cleanupMessagingData,
    handleLiveChatFailure,
  ]);
};
